import { useCallback, useEffect, useRef } from 'react'
import { AppState, AppStateStatus } from 'react-native'
import { isWeb } from '@mythical/ui'
import { useStores } from 'app/store'

const MIN_REFRESH_INTERVAL_MS = 10_000

/**
 * Refresh user data when the app/tab returns to the foreground.
 * This ensures upgrades made outside the app are picked up on resume.
 */
export function useRefreshUserOnFocus() {
  const { userStore } = useStores()
  const lastRefreshAtRef = useRef(0)
  const isRefreshingRef = useRef(false)

  const refreshIfNeeded = useCallback(async (force = false) => {
    if (!userStore?.user?.id || userStore.isAuthInProgress) return
    if (isRefreshingRef.current) return

    const now = Date.now()
    if (!force && now - lastRefreshAtRef.current < MIN_REFRESH_INTERVAL_MS) {
      return
    }
    lastRefreshAtRef.current = now
    isRefreshingRef.current = true

    try {
      await userStore.refreshAccessToken()
    } catch (error) {
      console.error('[useRefreshUserOnFocus] refreshAccessToken error:', error)
    } finally {
      isRefreshingRef.current = false
    }
  }, [userStore])

  useEffect(() => {
    if (!userStore) return

    if (isWeb) {
      if (typeof window === 'undefined' || typeof document === 'undefined') return

      const handleFocus = () => refreshIfNeeded()
      const handleVisibility = () => {
        if (document.visibilityState === 'visible') {
          // Force refresh when the tab becomes active again (e.g. after checkout).
          refreshIfNeeded(true)
        }
      }

      window.addEventListener('focus', handleFocus)
      document.addEventListener('visibilitychange', handleVisibility)

      return () => {
        window.removeEventListener('focus', handleFocus)
        document.removeEventListener('visibilitychange', handleVisibility)
      }
    }

    const handleAppStateChange = (nextState: AppStateStatus) => {
      if (nextState === 'active') {
        // Force refresh when returning to foreground on native.
        refreshIfNeeded(true)
      }
    }

    const subscription = AppState.addEventListener('change', handleAppStateChange)

    return () => {
      subscription?.remove?.()
    }
  }, [userStore, refreshIfNeeded])
}
